import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import LightboxModal from '../components/LightboxModal'
import { useCms } from '../context/CmsContext'

export default function TattoosPage() {
  const { tattooStyles, getSection, getPage } = useCms()
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const pageData = getPage('tatoveringer', {})

  const heroSection = {
    title: pageData.heroTitle || getSection('tattoos_hero').title || 'Tatoveringer hos Docuit ink',
    subtitle: pageData.heroSubtitle || getSection('tattoos_hero').subtitle || 'Jeg mestrer en lang række kunstneriske discipliner og tilpasser altid motiv, kontur og skyggelægning til dine personlige ønsker og kroppens anatomi.'
  }

  const galleryImages = tattooStyles.map((style) => ({
    src: style.image,
    title: style.title,
    alt: style.title
  }))

  const openLightbox = (index) => {
    setLightboxIndex(index)
    setLightboxOpen(true)
  }

  return (
    <div className="min-h-screen bg-[#0f1013] text-[#f4f3f0] py-12 md:py-20">
      <SEO
        title="Tatoveringer & Stilarter | Black & Grey, Fine Line m.m. - Docuit ink"
        description="Udforsk de tatoveringsstilarter jeg tilbyder hos Docuit ink i Vester Sottrup nær Sønderborg. Specialiseret i Black & Grey realism, Fine Line, custom lettering og cover-ups."
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Page Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-serif font-extrabold text-white mb-4">
            {heroSection.title}
          </h1>
          {heroSection.subtitle && (
            <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
              {heroSection.subtitle}
            </p>
          )}
        </div>

        {/* Style Showcase Rows */}
        <div className="space-y-16">
          {tattooStyles.map((style, index) => {
            const isLight = index % 2 === 1
            return (
              <div
                key={style.id || index}
                id={style.slug}
                className={`${
                  isLight
                    ? 'bg-[#f5f4f0] text-[#141519] border border-[#e2dec6]'
                    : 'bg-[#17191e] text-[#f4f3f0] border border-[#282b34]'
                } rounded-xl overflow-hidden grid grid-cols-1 lg:grid-cols-12 gap-8 items-center shadow-lg`}
              >
                <div
                  className={`lg:col-span-6 relative h-72 sm:h-96 overflow-hidden cursor-pointer group ${isLight ? 'lg:order-2' : 'lg:order-1'}`}
                  onClick={() => openLightbox(index)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') openLightbox(index)
                  }}
                  aria-label={`Forstør ${style.title}`}
                >
                  <img
                    src={style.image}
                    alt={style.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-[#cbb05e] text-[#0f1013] flex items-center justify-center shadow-xl transform scale-90 group-hover:scale-100 transition-transform">
                      <i className="fa-solid fa-magnifying-glass text-lg" aria-hidden="true" />
                    </span>
                  </div>
                  <div className={`absolute inset-0 bg-gradient-to-t ${isLight ? 'from-[#f5f4f0]/80' : 'from-[#17191e]'} via-transparent to-transparent opacity-60 pointer-events-none`} />
                </div>

                <div className={`lg:col-span-6 p-6 sm:p-10 space-y-4 ${isLight ? 'lg:order-1' : 'lg:order-2'}`}>
                  <span className={`text-xs font-mono uppercase tracking-widest ${isLight ? 'text-[#a1822c]' : 'text-[#cbb05e]'} font-bold`}>
                    Stilart #{index + 1}
                  </span>
                  <h2 className={`text-2xl sm:text-3xl font-serif font-bold ${isLight ? 'text-[#141519]' : 'text-white'}`}>
                    {style.title}
                  </h2>
                  {style.tagline && (
                    <p className={`${isLight ? 'text-[#a1822c]' : 'text-[#cbb05e]'} text-xs sm:text-sm font-semibold`}>
                      {style.tagline}
                    </p>
                  )}
                  {style.description && (
                    <p className={`${isLight ? 'text-neutral-700' : 'text-neutral-300'} text-sm leading-relaxed`}>
                      {style.description}
                    </p>
                  )}

                  <div className="pt-4 flex flex-wrap gap-4">
                    <Link
                      to={`/galleri?stil=${style.id}`}
                      className={`inline-flex items-center gap-2 px-5 py-2.5 ${
                        isLight
                          ? 'bg-[#17191e] text-white hover:bg-[#cbb05e] hover:text-[#0f1013] border border-[#17191e]'
                          : 'bg-neutral-900 text-white border border-neutral-700 hover:border-[#cbb05e]'
                      } text-xs font-semibold uppercase tracking-wider rounded-full transition-colors`}
                    >
                      <i className="fa-solid fa-images text-[#cbb05e]" aria-hidden="true" />
                      {style.buttonText || `Se ${style.title} i galleriet`}
                    </Link>

                    <Link
                      to="/booking"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#cbb05e] hover:bg-[#d8be65] text-[#0f1013] text-xs font-bold uppercase tracking-wider rounded-full transition-colors shadow-md"
                    >
                      {style.secondaryButtonText || 'Book denne stil'}
                    </Link>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Lightbox Modal */}
        <LightboxModal
          isOpen={lightboxOpen}
          currentIndex={lightboxIndex}
          images={galleryImages}
          onClose={() => setLightboxOpen(false)}
          onIndexChange={(newIdx) => setLightboxIndex(newIdx)}
        />
      </div>
    </div>
  )
}
